<template>
  <div
    ref="root"
    class="form-control"
    :class="[
      {
        // inputFocused: focused && !discreetInputs,
        open: dropdownVisible,
        discreet: discreetInputs,
        empty: !modelValue?.length,
        'modified modified-bg': !multiple && modelValue?.[0] && isModifiedValue(modelValue[0]),
        focused: focused && !discreetInputs,
      },
      multiple ? 'multi-valued' : 'single-valued',
    ]"
    @mousedown.right.prevent
    @contextmenu="inputRef?.blur()"
    @mousedown.left.exact.prevent.stop
    @click.left.exact.prevent.stop="setInputFocused(!dropdownVisible)"
  >
    <div ref="tagsContainer" class="tags-container" :class="[innerClass]">
      <template v-if="multiple">
        <div
          v-for="(value, i) of modelValue"
          :key="i"
          class="square-tag"
          :class="[
            {
              'modified-glossy-bg modified-border-thin': isModifiedValue(value),
            },
            tagClass?.(value),
          ]"
          @mousedown.stop
          @click.stop
          @keydown.space.prevent="removeOption(i)"
        >
          <!-- @keydown.exact.tab.stop -->
          <!-- @keydown.shift.tab="i > 0 ? $event.stopPropagation() : 0" -->
          <span class="content" :class="{ 'line-through': inversedOptions }">
            <i v-if="selectedOptions?.[i]?.icon" class="mr-1" :class="selectedOptions?.[i].icon" />
            {{ optionsByValue[toComparable(value)]?.displayValue || stripMapping(value) }}

            <OptionFlagsComponent :option="selectedOptions?.[i]" />
          </span>
          <span class="remove" @click.stop="removeOption(i)">&times;</span>
        </div>
      </template>

      <template v-else-if="!searchTerm && selectedOptions?.length">
        <div v-for="option of selectedOptions" :key="option.value" :class="{ 'line-through': inversedOptions }">
          <i v-if="option.icon" class="mr-1" :class="option.icon" />
          <span v-html="option.displayValue" />
          <OptionFlagsComponent :option="option" />
        </div>
      </template>

      <input
        :id="id"
        ref="inputRef"
        v-model="searchTerm"
        :type="inputType"
        step="1"
        class="search"
        :placeholder="modelValue?.length ? undefined : placeholder"
        autocomplete="off"
        :maxlength="maxLength"
        @input="handleTextInput"
        @keydown="keydownHandler"
        @focus="setInputFocused(true)"
        @blur="setInputFocused(false)"
      />
    </div>

    <div class="toggle-button">
      <span class="separator" />
      <div class="px-2">
        <i class="fa fa-chevron-down" />
      </div>
    </div>

    <div
      v-show="dropdownVisible"
      ref="listRef"
      class="dropdown-list minimalistic"
      :class="{ 'position-fixed': fixed, reversed: reverseDropdown }"
      @mousedown.prevent.stop
    >
      <!-- Options -->
      <div
        v-for="(option, i) of filteredOptions"
        :key="i"
        :ref="(el) => (itemRefs[i] = el as HTMLElement)"
        class="list-item"
        :class="{
          selected: selectedIndex === i,
          disabled: valueIsAlreadyAdded(option.value) || option.disabled || (!option.value && !modelValue),
          staged: option.staged,
          'font-italic ': option.value === undefined,
          'text-muted': !option.value && selectedIndex !== i,
        }"
        @wheel.stop
        @mouseenter="kbNavigating ? 0 : (selectedIndex = i)"
        @mousedown.stop.prevent="selectOption(option)"
        @click.stop.prevent
      >
        <i v-if="option.icon" class="mr-1" :class="option.icon" />
        <small v-if="option.parent && displayParents" class="d-block w-100"> {{ option.parent }} </small>
        <span v-html="option.displayValue" />

        <OptionFlagsComponent :option="option" />

        <button v-if="option.staged" tabindex="0" class="ml-auto link remove mr-1" @mousedown.prevent.stop @click.stop="emit('hide-staged', { value: option })">
          <i class="fa fa-eye-slash" />
        </button>
      </div>
      <em v-if="!filteredOptions.length" class="disabled d-inline-block">{{ appTranslate('No options found') }}</em>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TValue extends string | number">
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { Option } from '@/models/Global'
import { areComparable, arrayToRecord, clamp, stripMapping, toComparable, toSafeString, valueIsEmpty } from '@/utils/tools'
import { computed, nextTick, ref, watch } from 'vue'
import OptionFlagsComponent from './simple/OptionFlagsComponent.vue'

// -----------
// -- Setup --
// -----------

type ModelValue = string | TValue[] | null

const emit = defineEmits<{
  'update:modelValue': [value: ModelValue]
  select: [option: { value: Option<TValue> }]
  // remove: [option: { value: Option<TValue> }]
  input: [value: { value: ModelValue }]
  search: [term: { value: string | number }]
  new: [option: { value: Option<TValue> }]
  'hide-staged': [option: { value: Option<TValue> }]
}>()

const props = withDefaults(
  defineProps<{
    modelValue?: ModelValue
    options: Option<TValue>[]
    originalOptions?: Set<string | number>
    allowCustomValues?: 'accept' | 'confirm' | false
    multiple?: boolean
    discreetInputs?: boolean
    placeholder?: string
    nullable?: boolean // whether modelValue can be nulled
    deletable?: boolean // whether values can be removed
    inputType?: 'search' | 'text' | 'number' | 'date' | 'week'
    innerClass?: string
    id?: string // for use with <label :for="id">
    fixed?: boolean // positions the dropdown with fixed positioning. closes on scroll if used. uses ".custom-modal .body"
    tagClass?: (_tag: string | number) => string
    customValuePrefix?: string
    reverseDropdown?: boolean
    inversedOptions?: boolean
    maxLength?: number
  }>(),
  { inputType: 'search', nullable: true, deletable: true, customValuePrefix: 'Add option' },
)

const { appTranslate } = useTranslation()
const { setDropdownDirection } = useTools()

// ---------------
// -- Reactives --
// ---------------
const dropdownVisible = ref(false)
const searchTerm = ref<string | number>('')
const selectedIndex = ref(-1)

const inputRef = ref<HTMLInputElement>()
const itemRefs = ref<Array<HTMLElement>>([])
const focused = ref(false)

const kbNavigating = ref(false)

const root = ref<HTMLElement>()
const listRef = ref<HTMLElement>()

// --------------------
// -- Computed props --
// --------------------
const comparableSearchTerm = computed(() => toComparable(searchTerm.value))

const matchesSearch = (value: string | number) => toComparable(value)?.includes(comparableSearchTerm.value)

const displayParents = computed(() => !!searchTerm.value)

const filteredOptions = computed(() => {
  if (!valueIsEmpty(searchTerm.value)) {
    // const availableOptions = props.options.filter((option) => matchesSearch(option.displayValue) || matchesSearch(option.value))
    const availableOptions = props.options.reduce<Option<TValue>[]>((acc, option) => {
      if (matchesSearch(option.displayValue) || matchesSearch(option.value)) {
        if (option.parent && !matchesSearch(option.parent)) {
          acc.push(option)
        } else {
          acc.push({ ...option, parent: undefined })
        }
      }

      return acc
    }, [])

    if (props.allowCustomValues) {
      const hasExactMatch = availableOptions.some(
        (option) => toComparable(option.displayValue) === comparableSearchTerm.value || toComparable(option.value) === comparableSearchTerm.value,
      )

      const term = toSafeString(searchTerm.value)?.trim()
      if (!hasExactMatch && term) {
        availableOptions.push({
          displayValue: `<span class="add-option">${appTranslate(props.customValuePrefix)}:</span> "${term}"`,
          value: term as TValue,
        })
      }
    }

    return availableOptions
  }

  return props.options
})

const optionsByValue = computed(() => arrayToRecord(props.options, 'value', toComparable))

const modelValueArr = computed((): TValue[] | null => {
  return Array.isArray(props.modelValue) ? props.modelValue : props.modelValue ? [props.modelValue as TValue] : null
})

const comparableModelValueArr = computed(() => modelValueArr.value?.map(toComparable))

const comparableModelValueSet = computed(() => new Set(comparableModelValueArr.value))

const selectedOptions = computed(() =>
  comparableModelValueArr.value?.map((value) => optionsByValue.value[value] || ({ displayValue: value } as Option<TValue>)),
)

const selectedElement = computed(() => {
  if (selectedIndex.value >= 0) {
    return itemRefs.value?.[selectedIndex.value]
  }
  return undefined
})

// const modelValueShouldBeArray = computed(() => props.multiple || Array.isArray(props.modelValue))

// --------------
// -- Watchers --
// --------------

watch(
  () => props.options,
  () => {
    if (dropdownVisible.value && !searchTerm.value) {
      // if options change (happens after async fetch after opening the dropdown), a second scroll to selected is necessary
      findAndScrollToSelected()
    }
  },
)

watch(filteredOptions, () => {
  selectedIndex.value = Math.min(selectedIndex.value, filteredOptions.value.length - 1)
})

watch(dropdownVisible, () => {
  if (!dropdownVisible.value) {
    return
  }

  if (props.fixed && listRef.value && root.value) {
    const parentRect = root.value.getBoundingClientRect()
    const scrollTop = root.value.closest('.custom-modal .body')?.scrollTop || 0

    document.addEventListener(
      'wheel',
      () => {
        dropdownVisible.value = false
      },
      { once: true },
    )

    Object.assign(listRef.value?.style, {
      marginTop: `${parentRect.height - scrollTop}px`,
      // top: `${parentRect.bottom}px`,
      // left: `${parentRect.left}px`,
      width: `${parentRect.width - 3}px`,
      zIndex: 1001,
    })

    setDropdownDirection(root.value, listRef.value)
  }

  findAndScrollToSelected()
})

// ---------------
// -- Functions --
// ---------------

const setInputFocused = (newState: boolean) => {
  focused.value = newState
  dropdownVisible.value = newState

  if (newState) {
    inputRef.value?.focus()
  } else {
    // inputRef.value?.blur()
    searchTerm.value = ''
  }
}

const isModifiedValue = (value: string | number) => props.originalOptions && !props.originalOptions?.has(value)

const valueIsAlreadyAdded = (value: string | number | undefined) => (value ? comparableModelValueSet.value.has(toComparable(value)) : !props.modelValue)

const keydownHandler = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    if (searchTerm.value) {
      event.stopPropagation()
    }
  } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault()

    const deltaChange = event.key === 'ArrowUp' ? -1 : 1

    if (dropdownVisible.value) {
      kbNavigating.value = true
      selectedIndex.value = clamp(selectedIndex.value + deltaChange, 0, filteredOptions.value.length - 1)

      scrollSelectedOptionIntoView()

      setTimeout(() => {
        // delay to hinder mouse events from interfering
        kbNavigating.value = false
      }, 100)
    } else {
      dropdownVisible.value = true
    }
  } else if (event.key === 'Enter' && selectedIndex.value > -1) {
    if (dropdownVisible.value) {
      event.preventDefault()
      const option = filteredOptions.value[selectedIndex.value]
      option && selectOption(option)
    }
  } else if (event.key === 'Backspace') {
    event.stopPropagation()
    if (!searchTerm.value && props.modelValue) {
      removeOption(Array.isArray(props.modelValue) ? props.modelValue.length - 1 : 0)
    }
  } else if (event.key === 'Tab' && props.multiple) {
    if (focused.value && event.shiftKey && props.modelValue?.length) {
      event.stopPropagation()
    }
  } else if (event.key === 'Escape') {
    if (dropdownVisible.value) {
      event.preventDefault()
      inputRef?.value?.blur()
    }
  } else if (event.key?.length === 1) {
    // key can be undefined if autofill is used
    event.stopPropagation()
  }
}

const handleTextInput = () => {
  emit('search', { value: searchTerm.value })
  dropdownVisible.value = !valueIsEmpty(searchTerm.value)
  if (!valueIsEmpty(searchTerm.value) && selectedIndex.value === -1) {
    selectedIndex.value = 0
  }
}

const selectOption = (option: Option<TValue>) => {
  const value = option.value //?.toString()
  // if (!valueIsAlreadyAdded(option.value) && !option.disabled && (value === undefined || !props.modelValue?.includes(value))) {
  if (!valueIsAlreadyAdded(option.value) && !option.disabled) {
    selectedIndex.value = props.options.findIndex((otherOption) => areComparable(otherOption.value, value))

    const confirmMsg = appTranslate("You're adding a new value. Make sure the value is in english if possible! Translation will be added by an administrator.")

    if (selectedIndex.value > -1 || props.allowCustomValues !== 'confirm' || confirm(confirmMsg)) {
      searchTerm.value = ''
      dropdownVisible.value = false

      // const updateValue: ModelValue = !props.multiple || value === undefined ? value : props.multiple ? props.modelValue?.concat(value) || [value] : value

      let updateValue: ModelValue

      if (props.multiple) {
        const current = Array.isArray(props.modelValue) ? props.modelValue : []
        updateValue = value === undefined ? current : [...current, value]
      } else {
        updateValue = value === undefined ? (props.nullable ? null : '') : String(value)
      }

      emit('update:modelValue', updateValue)
      emit('select', { value: option })
      emit('input', { value: updateValue })
      selectedIndex.value === -1 && emit('new', { value: option })

      return true
    }
  }
}

const removeOption = async (index: number) => {
  if (modelValueArr.value && props.deletable) {
    // const removed = modelValueArr.value[index]

    const filteredModelValueArr = modelValueArr.value.filter((_, i) => i !== index)
    dropdownVisible.value = false

    const updateValue: ModelValue =
      !filteredModelValueArr.length && props.nullable ? null : props.multiple ? filteredModelValueArr : filteredModelValueArr[0].toString()

    emit('update:modelValue', updateValue)
    // emit('remove', { value: removed })
    emit('input', { value: updateValue })
  }
}

const scrollSelectedOptionIntoView = async () => {
  await nextTick()
  if (dropdownVisible.value) {
    selectedElement.value?.scrollIntoView({
      block: 'nearest',
    })
  }
}

const findAndScrollToSelected = () => {
  if (modelValueArr.value) {
    // set selected index of selected value on open (last selected if multiple)
    const selectedValue = modelValueArr.value[modelValueArr.value.length - 1]
    selectedIndex.value = filteredOptions.value.findIndex((option) => option.value == selectedValue)
    scrollSelectedOptionIntoView()
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:color';

.form-control {
  position: relative;

  color: inherit;
  display: flex;

  padding: 0;
  max-height: 250px;
  height: unset;
  cursor: default;

  &.multi-valued {
    .search {
      width: 75px;
    }
  }

  &:not(.discreet) {
    min-height: calc(1.5rem + 0.75rem + 2px);

    &.multi-valued .tags-container {
      padding: 2px 3px;
    }

    &.single-valued .tags-container,
    &.multi-valued.empty .tags-container {
      padding: 0.375rem 0.75rem; // same padding as form-control. use on singular valued and empty wrappers only since tags adds extra padding
    }
  }

  .tags-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    overflow-y: auto;
    overflow-x: auto;
    box-sizing: content-box;
    margin-right: 2px;

    .square-tag {
      // background-color: #e5e5e5;
      // glossy effect
      $bgColour: #eee;
      background: linear-gradient(
        color.scale($bgColour, $lightness: 20%),
        color.scale($bgColour, $lightness: -4%) 75%,
        color.scale($bgColour, $lightness: -3%)
      );

      color: unset;
      user-select: none;
      box-shadow: inset 0 0 3px #fffa;
      border: 1px solid #aaa;
      border-radius: 2px;
      display: inline-flex;
      margin: 2px;
      align-items: center;
      word-break: break-all;

      .content {
        padding: 2px 3px 2px 6px;
      }

      .remove {
        padding: 2px 6px;
        display: inline-flex;
        align-items: center;
        border: none;
        background-color: transparent;
        line-height: 1em;
        font-size: 1.6em;
        outline: none;
        height: 100%;

        &:hover,
        &:focus {
          color: white;
          background-color: #e81123aa;
        }
      }

      &:focus {
        outline: 1px solid #aaa;
      }
    }
  }

  .search {
    display: flex;
    border: none;
    flex: 1 1 0;
    width: 0;
    box-sizing: border-box;
    cursor: default;
    appearance: textfield;
    -moz-appearance: textfield;
    color: inherit;

    border: none !important;
    outline: none !important;
    background-color: inherit;

    // remove x
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }

    // remove number arrows
    ::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  .dropdown-list {
    $shadow-colour: #0003;

    z-index: 1000;

    font-style: normal;
    // color: var(--text-colour);
    background-color: white;
    border: 1px solid #ddd;
    border-top: none;
    box-sizing: content-box;
    max-height: 16.2em;
    overflow-y: auto;
    box-shadow: $shadow-colour 0 2px 10px;
    overscroll-behavior: none;

    &:not(.position-fixed) {
      position: absolute;
      top: calc(100% + 2px);
      left: -1px;
      width: 100%;
    }

    > * {
      padding: 0.15rem 0.25rem;
    }

    &.reversed {
      transform: translateY(calc(-100% - 38px));
      box-shadow: $shadow-colour 0 -2px 10px;
    }

    .list-item {
      width: 100%;
      min-width: fit-content;
      padding-right: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      &.selected {
        text-shadow: none;
        color: white;
        background-color: #59f;
      }

      .remove {
        opacity: 0.5;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .toggle-button {
    font-size: 10px;
    color: var(--text-colour);
    margin-left: auto;
    margin-right: 0;
    display: flex;
    align-items: center;

    .separator {
      width: 1px;
      height: 100%;
      background-color: var(--light-grey);
    }
  }
}

.staged {
  font-style: italic;

  &:not(.disabled) {
    color: var(--new-colour);
  }
}

:deep() {
  .list-item {
    &:not(.selected) .add-option {
      color: var(--new-colour) !important;
    }

    &.selected .add-option {
      text-shadow: 1px 1px 2px var(--new-colour);
    }
  }
}
</style>
